import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Clock, Navigation, ArrowRight } from 'lucide-react';

function MapPreview({ origin, destination, distance, duration, durationDesc, originCoords: providedOriginCoords, destCoords: providedDestCoords, isDarkMode = false, paymentAmount, ratePerMile }) {
  const [routeGeometry, setRouteGeometry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [map, setMap] = useState(null);
  const mapContainer = useRef(null);

  console.log('[MapPreview] Component rendered with:', {
    origin,
    destination,
    distance,
    originCoords: providedOriginCoords,
    destCoords: providedDestCoords,
    isDarkMode,
    paymentAmount,
    ratePerMile
  });

  // Fetch actual driving route from Mapbox
  const fetchDrivingRoute = async (originCoords, destCoords) => {
    const MAPBOX_API_KEY = process.env.REACT_APP_MAPBOX_PUBLIC_KEY;
    
    if (!MAPBOX_API_KEY) {
      console.warn('Mapbox API key not found, using straight line');
      return null;
    }

    try {
      const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${originCoords.lon},${originCoords.lat};${destCoords.lon},${destCoords.lat}`;
      const params = new URLSearchParams({
        access_token: MAPBOX_API_KEY,
        geometries: 'geojson'
      });
      
      const response = await fetch(`${url}?${params}`);
      const data = await response.json();
      
      if (data.routes && data.routes.length > 0) {
        return data.routes[0].geometry.coordinates;
      }
      return null;
    } catch (error) {
      console.error('Error fetching route:', error);
      return null;
    }
  };

  // Calculate approximate route points for visualization fallback
  const generateRoutePoints = (originCoords, destCoords, numPoints = 5) => {
    const points = [];
    for (let i = 0; i <= numPoints; i++) {
      const ratio = i / numPoints;
      const lat = originCoords.lat + (destCoords.lat - originCoords.lat) * ratio;
      const lon = originCoords.lon + (destCoords.lon - originCoords.lon) * ratio;
      points.push({ lat, lon });
    }
    return points;
  };

  // Fallback coordinates for visualization
  const getCoordinates = (cityState) => {
    const coords = {
      'Columbus, OH': { lat: 39.9612, lon: -82.9988 },
      'Chicago, IL': { lat: 41.8781, lon: -87.6298 },
      'San Diego, CA': { lat: 32.7157, lon: -117.1611 },
      'Denver, CO': { lat: 39.7392, lon: -104.9903 },
      'Atlanta, GA': { lat: 33.7490, lon: -84.3880 },
      'Dallas, TX': { lat: 32.7767, lon: -96.7970 },
      'Miami, FL': { lat: 25.7617, lon: -80.1918 },
      'Seattle, WA': { lat: 47.6062, lon: -122.3321 }
    };
    return coords[cityState] || coords['Columbus, OH'];
  };

  // Use provided coordinates if available, otherwise fall back to hardcoded ones
  const originCoords = providedOriginCoords || getCoordinates(origin);
  const destCoords = providedDestCoords || getCoordinates(destination);

  // Fetch route geometry when origin or destination changes
  useEffect(() => {
    if (!originCoords || !destCoords) {
      console.log('[MapPreview] Skipping route fetch - missing coordinates');
      return;
    }
    
    const loadRoute = async () => {
      console.log('[MapPreview] Fetching route geometry from', originCoords, 'to', destCoords);
      setLoading(true);
      const geometry = await fetchDrivingRoute(originCoords, destCoords);
      console.log('[MapPreview] Route geometry received:', geometry ? `${geometry.length} points` : 'null');
      setRouteGeometry(geometry);
      setLoading(false);
    };
    
    loadRoute();
  }, [origin, destination, originCoords, destCoords]);

  // Initialize Mapbox map
  useEffect(() => {
    const MAPBOX_API_KEY = process.env.REACT_APP_MAPBOX_PUBLIC_KEY;
    
    console.log('[MapPreview] Initializing map with API key:', MAPBOX_API_KEY ? 'Present' : 'Missing');
    console.log('[MapPreview] Map container ref:', mapContainer.current ? 'Present' : 'Missing');
    
    if (!MAPBOX_API_KEY || !mapContainer.current) {
      console.warn('[MapPreview] Missing API key or container, skipping map initialization');
      return;
    }

    // Check if token is public (starts with pk.)
    if (!MAPBOX_API_KEY.startsWith('pk.')) {
      console.error('Mapbox GL requires a public access token (pk.*), not a secret token (sk.*)');
      return;
    }

    console.log('[MapPreview] Starting mapbox-gl import...');
    
    // Dynamically import mapbox-gl to avoid SSR issues
    import('mapbox-gl').then((mapboxgl) => {
      console.log('[MapPreview] Mapbox GL imported successfully');
      mapboxgl.default.accessToken = MAPBOX_API_KEY;
      
      // Use default center if coordinates not available yet
      const defaultCenter = originCoords ? [originCoords.lon, originCoords.lat] : [-95.7129, 37.0902]; // Center of US
      console.log('[MapPreview] Creating map instance with center:', defaultCenter);
      
      const mapInstance = new mapboxgl.default.Map({
        container: mapContainer.current,
        style: isDarkMode ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/streets-v12',
        center: defaultCenter,
        zoom: 4
      });

      mapInstance.on('load', () => {
        console.log('[MapPreview] Map loaded successfully');
        setMap(mapInstance);
      });

      mapInstance.on('error', (e) => {
        console.error('[MapPreview] Mapbox error:', e);
      });

      return () => {
        console.log('[MapPreview] Cleaning up map instance');
        mapInstance.remove();
      };
    }).catch((error) => {
      console.error('[MapPreview] Failed to load Mapbox GL:', error);
    });
  }, []); // Only initialize once

  // Update map center when coordinates change
  useEffect(() => {
    if (!map || !originCoords || !destCoords) return;
    
    console.log('[MapPreview] Updating map center to:', [originCoords.lon, originCoords.lat]);
    map.setCenter([originCoords.lon, originCoords.lat]);
  }, [map, originCoords, destCoords]);

  // Update map style when dark mode changes
  useEffect(() => {
    if (!map) return;
    
    const newStyle = isDarkMode ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/streets-v12';
    console.log('[MapPreview] Updating map style to:', newStyle);
    map.setStyle(newStyle);
  }, [map, isDarkMode]);


  // Add driving route to map with payment/rate information
  const addDrivingRoute = async (map, originCoords, destCoords) => {
    console.log('[MapPreview] Adding driving route from', originCoords, 'to', destCoords);
    
    // Clear existing route if any
    try {
      const layersToRemove = ['route-label', 'route', 'origin-marker', 'destination-marker'];
      const sourcesToRemove = ['route-label', 'route', 'origin-marker', 'destination-marker'];
      
      layersToRemove.forEach(layer => {
        if (map.getLayer(layer)) {
          map.removeLayer(layer);
        }
      });
      
      sourcesToRemove.forEach(source => {
        if (map.getSource(source)) {
          map.removeSource(source);
        }
      });
    } catch (e) {
      console.log('[MapPreview] No existing route to clear');
    }
    
    const MAPBOX_API_KEY = process.env.REACT_APP_MAPBOX_PUBLIC_KEY;
    if (!MAPBOX_API_KEY) {
      console.warn('[MapPreview] No Mapbox API key available for route drawing');
      return;
    }
    
    // Use Mapbox Directions API
    const directionsUrl = `https://api.mapbox.com/directions/v5/mapbox/driving/${originCoords.lon},${originCoords.lat};${destCoords.lon},${destCoords.lat}?geometries=geojson&access_token=${MAPBOX_API_KEY}`;
    
    try {
      const response = await fetch(directionsUrl);
      const data = await response.json();
      
      if (data.routes && data.routes.length > 0) {
        const route = data.routes[0];
        
        // Add route source and layer
        map.addSource('route', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {
              distance: (route.distance / 1609.34).toFixed(1), // Convert to miles
              duration: Math.round(route.duration / 60) // Convert to minutes
            },
            geometry: route.geometry
          }
        });
        
        map.addLayer({
          id: 'route',
          type: 'line',
          source: 'route',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#3b82f6',
            'line-width': 4,
            'line-opacity': 0.75
          }
        });
        
        // Add route info label at midpoint if payment/rate data available
        if (paymentAmount && ratePerMile) {
          const midpoint = route.geometry.coordinates[Math.floor(route.geometry.coordinates.length / 2)];
          
          map.addSource('route-label', {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {
                payment: paymentAmount,
                rate: ratePerMile,
                distance: (route.distance / 1609.34).toFixed(0)
              },
              geometry: {
                type: 'Point',
                coordinates: midpoint
              }
            }
          });
          
          map.addLayer({
            id: 'route-label',
            type: 'symbol',
            source: 'route-label',
            layout: {
              'text-field': `$${paymentAmount.toLocaleString()}\n$${ratePerMile.toFixed(2)}/mi`,
              'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
              'text-size': 12,
              'text-anchor': 'center',
              'text-offset': [0, -1]
            },
            paint: {
              'text-color': '#ffffff',
              'text-halo-color': '#3b82f6',
              'text-halo-width': 2
            }
          });
        }
        
        // Add origin marker with label
        map.addSource('origin-marker', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {
              label: 'ORIGIN',
              city: origin
            },
            geometry: {
              type: 'Point',
              coordinates: [originCoords.lon, originCoords.lat]
            }
          }
        });
        
        map.addLayer({
          id: 'origin-marker',
          type: 'circle',
          source: 'origin-marker',
          paint: {
            'circle-radius': 12,
            'circle-color': '#22c55e',
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff'
          }
        });
        
        
        // Add destination marker with label
        map.addSource('destination-marker', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {
              label: 'DESTINATION',
              city: destination
            },
            geometry: {
              type: 'Point',
              coordinates: [destCoords.lon, destCoords.lat]
            }
          }
        });
        
        map.addLayer({
          id: 'destination-marker',
          type: 'circle',
          source: 'destination-marker',
          paint: {
            'circle-radius': 12,
            'circle-color': '#ef4444',
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff'
          }
        });
        
        
        // Fit map to show entire route with padding
        const coordinates = route.geometry.coordinates;
        
        // Create bounds ensuring proper [lng, lat] format
        const bounds = new window.mapboxgl.LngLatBounds();
        
        // Add origin and destination points to bounds
        bounds.extend([originCoords.lon, originCoords.lat]);
        bounds.extend([destCoords.lon, destCoords.lat]);
        
        // Also extend bounds with route coordinates if needed
        coordinates.forEach(coord => {
          if (Array.isArray(coord) && coord.length >= 2) {
            bounds.extend(coord);
          }
        });
        
        map.fitBounds(bounds, {
          padding: 50
        });
        
        console.log('[MapPreview] Route added successfully');
      }
    } catch (error) {
      console.error('[MapPreview] Error fetching route:', error);
    }
  };

  // Update map with route data
  useEffect(() => {
    console.log('[MapPreview] Route update effect triggered:', { map: !!map, originCoords, destCoords, paymentAmount, ratePerMile });
    if (!map || !originCoords || !destCoords) {
      console.log('[MapPreview] Skipping route update - missing requirements:', { map: !!map, originCoords: !!originCoords, destCoords: !!destCoords });
      return;
    }
    
    console.log('[MapPreview] Calling addDrivingRoute with coordinates:', originCoords, destCoords);
    // Add driving route with payment information
    addDrivingRoute(map, originCoords, destCoords);
  }, [map, originCoords, destCoords, paymentAmount, ratePerMile]);

  // Legacy route data effect (keeping for backwards compatibility)
  useEffect(() => {
    if (!map || !routeGeometry) return;

    // Remove existing route if any
    if (map.getSource('route')) {
      map.removeLayer('route');
      map.removeSource('route');
    }
    if (map.getSource('start')) {
      map.removeLayer('start');
      map.removeSource('start');
    }
    if (map.getSource('end')) {
      map.removeLayer('end');
      map.removeSource('end');
    }

    // Add route
    map.addSource('route', {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: routeGeometry
        }
      }
    });

    map.addLayer({
      id: 'route',
      type: 'line',
      source: 'route',
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#3b82f6',
        'line-width': 5,
        'line-opacity': 0.75
      }
    });

    // Add start point
    map.addSource('start', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Point',
            coordinates: [originCoords.lon, originCoords.lat]
          }
        }]
      }
    });

    map.addLayer({
      id: 'start',
      type: 'circle',
      source: 'start',
      paint: {
        'circle-radius': 10,
        'circle-color': '#22c55e'
      }
    });

    // Add end point
    map.addSource('end', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Point',
            coordinates: [destCoords.lon, destCoords.lat]
          }
        }]
      }
    });

    map.addLayer({
      id: 'end',
      type: 'circle',
      source: 'end',
      paint: {
        'circle-radius': 10,
        'circle-color': '#ef4444'
      }
    });

    // Fit map to show entire route
    import('mapbox-gl').then((mapboxgl) => {
      const coordinates = routeGeometry;
      // Create bounds ensuring proper [lng, lat] format
      const bounds = new mapboxgl.default.LngLatBounds();
      
      // Extend bounds with route coordinates
      coordinates.forEach(coord => {
        if (Array.isArray(coord) && coord.length >= 2) {
          bounds.extend(coord);
        }
      });

      // Only fit bounds if we have valid coordinates
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, {
          padding: 50
        });
      }
    });

  }, [map, routeGeometry, originCoords, destCoords]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-500 to-primary-600 dark:from-primary-600 dark:to-primary-700 px-6 py-4">
        <div className="flex items-center space-x-2">
          <Navigation className="w-5 h-5 text-white" />
          <h3 className="text-lg font-semibold text-white">Route Preview</h3>
        </div>
      </div>

      {/* Route Info */}
      <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Distance</p>
              <p className="font-semibold text-gray-900 dark:text-white">{Math.round(distance).toLocaleString()} miles</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Drive Time</p>
              <p className="font-semibold text-gray-900 dark:text-white">{durationDesc}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <ArrowRight className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Route Type</p>
              <p className="font-semibold text-gray-900 dark:text-white">Fastest</p>
            </div>
          </div>
        </div>
      </div>

      {/* Map Visualization */}
      <div className="p-6">
        <div className="relative">
          <div 
            ref={mapContainer} 
            className="w-full h-80 rounded-lg border border-gray-200 dark:border-gray-600"
            style={{ 
              minHeight: '320px',
              backgroundColor: '#f0f0f0' // Debug background to see if container is visible
            }}
          />
          
          {loading && (
            <div className="absolute inset-0 bg-white dark:bg-gray-800 bg-opacity-75 dark:bg-opacity-75 flex items-center justify-center rounded-lg">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Loading route...</p>
              </div>
            </div>
          )}
          
          {(!process.env.REACT_APP_MAPBOX_PUBLIC_KEY || !process.env.REACT_APP_MAPBOX_PUBLIC_KEY.startsWith('pk.')) && (
            <div className="absolute inset-0 bg-gray-100 dark:bg-gray-700 flex items-center justify-center rounded-lg">
              <div className="text-center p-4">
                <MapPin className="w-8 h-8 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {!process.env.REACT_APP_MAPBOX_PUBLIC_KEY 
                    ? 'Mapbox API key required for map view'
                    : 'Mapbox GL requires a public token (pk.*), not secret token (sk.*)'
                  }
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Route Details */}
      <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-600 dark:text-gray-400">Estimated Fuel Cost</p>
            <p className="font-medium text-gray-900 dark:text-white">${(distance * 0.35).toFixed(2)}</p>
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400">Average Speed</p>
            <p className="font-medium text-gray-900 dark:text-white">{Math.round(distance / duration)} mph</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MapPreview;